import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { API_URL } from "../../apiConfig";
import { ArrowLeftIcon, UserPlusIcon, CheckCircleIcon, XMarkIcon, PlusIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";

const T = {
  ink:        "#1a1a1c",
  ink2:       "#4a4a50",
  ink3:       "#86868b",
  ink4:       "#b4b3b0",
  hair:       "rgba(20,20,22,0.07)",
  hair2:      "rgba(20,20,22,0.04)",
  surface:    "rgba(255,255,255,0.72)",
  surface2:   "#f9f8f6",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font:       "'Geist',-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  r:          18,
};

const cardStyle = {
  background: T.surface,
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  border: `1px solid ${T.hair}`,
  borderRadius: T.r,
  boxShadow: T.shadowMd,
};

const inputStyle = {
  width: "100%",
  padding: "9px 13px",
  borderRadius: 10,
  border: `1px solid ${T.hair}`,
  background: T.surface2,
  fontSize: 13.5,
  color: T.ink,
  fontFamily: T.font,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

const labelStyle = {
  fontSize: 11,
  color: T.ink3,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontWeight: 500,
  marginBottom: 5,
  display: "block",
};

const sectionHeaderStyle = {
  padding: "14px 20px",
  borderBottom: `1px solid ${T.hair}`,
  fontSize: 13,
  fontWeight: 600,
  color: T.ink,
};

function Field({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
const isStrongPassword = (v) => /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(String(v));

const AVATAR_PRESETS = ["#1e3a8a","#0f5c2e","#7c2d12","#4c1d95","#b45309","#0f766e","#be185d","#1d4ed8"];
function derivedColor(name = "") {
  const n = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PRESETS[n % AVATAR_PRESETS.length];
}
function avatarInitials(name = "") {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
}

export default function PartnerNew() {
  const navigate = useNavigate();
  const location = useLocation();

  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("partner");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedCities, setSelectedCities] = useState([]);
  const [officeCities, setOfficeCities] = useState([]);
  const [category, setCategory] = useState("");
  const [benefit, setBenefit] = useState("");
  const [benefitDescription, setBenefitDescription] = useState("");
  const [whatsappPhone, setWhatsappPhone] = useState("4440048500");
  const [whatsappMessage, setWhatsappMessage] = useState("Gostaria de saber mais sobre esse benefício da [Empresa] e a Euro Condomínios");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [customCity, setCustomCity] = useState("");
  const [partnerColor, setPartnerColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingCities, setLoadingCities] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  function pickPhoto(file) {
    if (!file) { setPhotoFile(null); return; }
    if (!file.type.startsWith("image/")) { setErr("Envie apenas imagens (JPG ou PNG)."); setPhotoFile(null); return; }
    if (file.size > 2 * 1024 * 1024) { setErr("Imagem muito grande. Tamanho máximo: 2 MB."); setPhotoFile(null); return; }
    setErr("");
    setPhotoFile(file);
  }

  function validateAll() {
    if (!company.trim()) return "Informe o nome da empresa.";
    if (!email.trim() || !isEmail(email)) return "Informe um e-mail válido.";
    if (!password || !isStrongPassword(password)) return "A senha deve ter pelo menos 8 caracteres e conter letras e números.";
    if (!role) return "Informe o perfil.";
    if (!contactName.trim()) return "Informe o nome do contato.";
    if (!phone.trim()) return "Informe o telefone.";
    if (selectedCities.length === 0) return "Selecione ao menos uma cidade.";
    if (!category.trim()) return "Informe a categoria.";
    if (!benefit.trim()) return "Informe o benefício oferecido.";
    if (!photoFile) return "Selecione uma imagem.";
    if (!whatsappPhone.trim()) return "Informe o telefone do WhatsApp.";
    if (!benefitDescription.trim()) return "Informe a descrição do benefício.";
    if (!whatsappMessage.trim()) return "Informe a mensagem padrão do WhatsApp.";
    return null;
  }

  useEffect(() => {
    if (!photoFile) return undefined;
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  useEffect(() => {
    setWhatsappMessage(
      company.trim()
        ? `Gostaria de saber mais sobre esse benefício da ${company} e a Euro Condomínios`
        : "Gostaria de saber mais sobre esse benefício da [Empresa] e a Euro Condomínios"
    );
  }, [company]);

  useEffect(() => {
    async function loadOfficeCities() {
      try {
        const resp = await fetch(`${API_URL}/office-cities`);
        if (resp.ok) {
          const data = await resp.json();
          setOfficeCities(data.cities || []);
        }
      } catch (e) {
        console.error("Erro ao carregar cidades:", e);
      } finally {
        setLoadingCities(false);
      }
    }
    loadOfficeCities();
  }, []);

  useEffect(() => {
    if (!whatsappPhone && phone) setWhatsappPhone(phone);
  }, [phone]);

  async function uploadPartnerPhoto(partnerId, token) {
    if (!photoFile) return;
    const formData = new FormData();
    formData.append("photo", photoFile);
    const uploadResp = await fetch(`${API_URL}/admin/partners/${partnerId}/photo`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!uploadResp.ok) {
      const payload = await uploadResp.json().catch(() => ({}));
      throw new Error(payload?.message || "Falha ao enviar foto do parceiro");
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setErr(""); setOk("");
    if (!API_URL) return setErr("API_URL não definida no frontend (.env).");
    const vErr = validateAll();
    if (vErr) return setErr(vErr);
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) return setErr("Sem token. Faça login novamente.");
      const r = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company, email, password, role, cities: selectedCities, phone, contactName, category, benefit, benefitDescription, whatsappPhone, whatsappMessage, color: partnerColor || undefined }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) return setErr(data?.message || "Falha ao cadastrar parceiro.");
      if (data?.id && photoFile) await uploadPartnerPhoto(data.id, token);
      setOk("Parceiro cadastrado com sucesso!");
      setPhotoFile(null); setPhotoPreview("");
      setTimeout(() => {
        const from = new URLSearchParams(location.search).get("from");
        navigate(from ? from : "/admin/partners", { replace: true });
      }, 800);
    } catch (error) {
      setErr(error?.message || "Falha de conexão com a API.");
    } finally {
      setLoading(false);
    }
  }

  function goBack() {
    const from = new URLSearchParams(location.search).get("from");
    navigate(from ? from : "/admin/partners", { replace: !!from });
  }

  return (
    <form onSubmit={handleSave} noValidate style={{ fontFamily: T.font }}>

      {/* Page title */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: "-0.025em", color: T.ink }}>Cadastrar parceiro</h1>
        <div style={{ fontSize: 13, color: T.ink3, marginTop: 4 }}>Preencha todos os campos obrigatórios</div>
      </div>

      {/* Alerts */}
      {err && (
        <div style={{ padding: "12px 16px", borderRadius: 12, background: T.dangerSoft, color: T.danger, fontSize: 13, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <XMarkIcon style={{ width: 15, height: 15, flexShrink: 0 }} />
          {err}
        </div>
      )}
      {ok && (
        <div style={{ padding: "12px 16px", borderRadius: 12, background: T.okSoft, color: T.ok, fontSize: 13, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleIcon style={{ width: 15, height: 15, flexShrink: 0 }} />
          {ok}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Conta de acesso */}
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>Conta de acesso</div>
          <div style={{ padding: "20px 20px 24px" }}>
            <div style={{ marginBottom: 16 }}>
              <Field label="Empresa *">
                <input style={inputStyle} required placeholder="Nome da empresa" value={company} onChange={e => setCompany(e.target.value)} />
              </Field>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Cor do avatar</label>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:partnerColor||derivedColor(company), color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:700, letterSpacing:"-0.02em", flexShrink:0, transition:"background 0.2s" }}>
                  {avatarInitials(company||"EP")}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  <button type="button" onClick={()=>setPartnerColor("")} title="Automático (padrão)"
                    style={{ width:28, height:28, borderRadius:"50%", background:derivedColor(company), border:!partnerColor?`3px solid ${T.ink}`:`2px solid transparent`, cursor:"pointer", position:"relative", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"border 0.15s" }}>
                    {!partnerColor && <span style={{ width:7, height:7, borderRadius:"50%", background:"#fff" }}/>}
                  </button>
                  {AVATAR_PRESETS.map(c => (
                    <button key={c} type="button" onClick={()=>setPartnerColor(c)} title={c}
                      style={{ width:28, height:28, borderRadius:"50%", background:c, border:partnerColor===c?`3px solid ${T.ink}`:`2px solid transparent`, cursor:"pointer", flexShrink:0, transition:"border 0.15s, transform 0.1s" }}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.15)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
                    />
                  ))}
                  <label title="Cor personalizada" style={{ width:28, height:28, borderRadius:"50%", border:`2px dashed ${T.ink3}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, background: partnerColor && !AVATAR_PRESETS.includes(partnerColor) ? partnerColor : "transparent", position:"relative" }}>
                    <input type="color" value={partnerColor||"#1e3a8a"} onChange={e=>setPartnerColor(e.target.value)} style={{ opacity:0, position:"absolute", inset:0, cursor:"pointer", width:"100%", height:"100%" }} />
                    {(!partnerColor || AVATAR_PRESETS.includes(partnerColor)) && <span style={{ fontSize:16, color:T.ink3, lineHeight:1 }}>+</span>}
                  </label>
                </div>
                {!partnerColor && <span style={{ fontSize:11.5, color:T.ink3 }}>Automático</span>}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 160px", gap: 14 }}>
              <Field label="E-mail *">
                <input style={{ ...inputStyle, borderColor: email && !isEmail(email) ? T.danger : undefined }} type="email" required placeholder="parceiro@empresa.com" value={email} onChange={e => setEmail(e.target.value)} />
                {email && !isEmail(email) && <div style={{ fontSize: 11.5, color: T.danger, marginTop: 4 }}>E-mail inválido</div>}
              </Field>
              <Field label="Senha *">
                <input style={{ ...inputStyle, borderColor: password && !isStrongPassword(password) ? T.danger : undefined }} type="password" required placeholder="Mín. 8 caracteres" value={password} onChange={e => setPassword(e.target.value)} />
                {password && !isStrongPassword(password)
                  ? <div style={{ fontSize: 11.5, color: T.danger, marginTop: 4 }}>Mín. 8 caracteres, letras e números</div>
                  : <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 4 }}>Letras + números, 8+ caracteres</div>}
              </Field>
              <Field label="Perfil">
                <select style={{ ...inputStyle, appearance: "none", WebkitAppearance: "none" }} value={role} onChange={e => setRole(e.target.value)}>
                  <option value="partner">parceiro</option>
                  <option value="admin">admin</option>
                </select>
              </Field>
            </div>
          </div>
        </div>

        {/* Informações de contato */}
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>Informações de contato</div>
          <div style={{ padding: "20px 20px 24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <Field label="Nome de contato *">
                <input style={inputStyle} required placeholder="Responsável" value={contactName} onChange={e => setContactName(e.target.value)} />
              </Field>
              <Field label="Telefone *">
                <input style={inputStyle} required placeholder="(00) 00000-0000" value={phone} onChange={e => setPhone(e.target.value)} />
              </Field>
            </div>

            {/* Cidades */}
            <div style={{ height: 1, background: T.hair, marginBottom: 20 }} />
            <div style={{ fontSize: 11, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500, marginBottom: 12 }}>Cidades onde atende *</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 8 }}>Selecione as cidades onde a empresa atua</div>
                {loadingCities ? (
                  <div style={{ fontSize: 13, color: T.ink3 }}>Carregando cidades…</div>
                ) : (
                  <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "10px 14px", maxHeight: 200, overflowY: "auto", background: T.surface2 }}>
                    {officeCities.map(cityObj => (
                      <label key={cityObj.value} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer", fontSize: 13, color: T.ink }}>
                        <input
                          type="checkbox"
                          checked={selectedCities.includes(cityObj.value)}
                          onChange={e => {
                            if (e.target.checked) setSelectedCities([...selectedCities, cityObj.value]);
                            else setSelectedCities(selectedCities.filter(c => c !== cityObj.value));
                          }}
                          style={{ accentColor: T.accent, width: 14, height: 14 }}
                        />
                        {cityObj.value}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 8 }}>Adicionar cidade personalizada</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder="Ex.: São Paulo-SP"
                    value={customCity}
                    onChange={e => setCustomCity(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = customCity.trim();
                        if (t && !selectedCities.includes(t)) { setSelectedCities([...selectedCities, t]); setCustomCity(""); }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const t = customCity.trim();
                      if (t && !selectedCities.includes(t)) { setSelectedCities([...selectedCities, t]); setCustomCity(""); }
                    }}
                    disabled={!customCity.trim()}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.hair}`, background: T.accent, color: "#fff", cursor: customCity.trim() ? "pointer" : "not-allowed", opacity: customCity.trim() ? 1 : 0.4, flexShrink: 0 }}
                  >
                    <PlusIcon style={{ width: 16, height: 16 }} />
                  </button>
                </div>
                <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "10px 14px", minHeight: 80, background: T.surface2 }}>
                  {selectedCities.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: T.ink4, textAlign: "center", paddingTop: 16 }}>Nenhuma cidade selecionada</div>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {selectedCities.map(city => (
                        <span key={city} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px 3px 10px", borderRadius: 999, background: T.accentSoft, color: T.accent, fontSize: 12.5, fontWeight: 500 }}>
                          {city}
                          <button type="button" onClick={() => setSelectedCities(selectedCities.filter(c => c !== city))}
                            style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", color: T.accent, cursor: "pointer", padding: 0, lineHeight: 1 }}>
                            <XMarkIcon style={{ width: 12, height: 12 }} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefício */}
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>Benefício</div>
          <div style={{ padding: "20px 20px 24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <Field label="Categoria *">
                <input style={inputStyle} required placeholder="Ex.: Energia, Segurança, Limpeza…" value={category} onChange={e => setCategory(e.target.value)} />
              </Field>
              <Field label="Benefício *">
                <input style={inputStyle} required placeholder="Título do benefício oferecido" value={benefit} onChange={e => setBenefit(e.target.value)} maxLength={50} />
              </Field>
            </div>
            <Field label="Descrição do benefício *">
              <textarea
                style={{ ...inputStyle, resize: "vertical", minHeight: 90, lineHeight: 1.55 }}
                required
                placeholder="Detalhes exibidos no site e no app"
                value={benefitDescription}
                onChange={e => setBenefitDescription(e.target.value)}
                maxLength={1000}
              />
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 4 }}>{benefitDescription.length}/1000 caracteres</div>
            </Field>
          </div>
        </div>

        {/* Comunicação digital */}
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>Comunicação digital</div>
          <div style={{ padding: "20px 20px 24px", display: "grid", gridTemplateColumns: "200px 1fr", gap: 14 }}>
            <Field label="WhatsApp (só números) *">
              <input style={inputStyle} required placeholder="5544999999999" value={whatsappPhone} onChange={e => setWhatsappPhone(e.target.value)} />
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 4 }}>Com DDD e código do país (55)</div>
            </Field>
            <Field label="Mensagem padrão do WhatsApp *">
              <textarea
                style={{ ...inputStyle, resize: "vertical", minHeight: 60, lineHeight: 1.55 }}
                required
                placeholder="Mensagem pré-preenchida ao abrir o chat"
                value={whatsappMessage}
                onChange={e => setWhatsappMessage(e.target.value)}
              />
            </Field>
          </div>
        </div>

        {/* Imagem */}
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>Imagem do card</div>
          <div style={{ padding: "20px 20px 24px", display: "grid", gridTemplateColumns: "1fr 220px", gap: 20, alignItems: "start" }}>
            <div>
              <Field label="Arquivo de imagem *">
                <label style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface2, cursor:"pointer" }}>
                  <input type="file" accept="image/*" required onChange={e => pickPhoto(e.target.files?.[0] || null)} style={{ display:"none" }} />
                  <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:7, background:T.surface, border:`1px solid ${T.hair}`, fontSize:13, fontWeight:500, color:T.ink2, flexShrink:0, whiteSpace:"nowrap" }}>
                    <ArrowUpTrayIcon style={{ width:13, height:13 }}/> Escolher arquivo
                  </span>
                  <span style={{ fontSize:13, color:photoFile ? T.ink : T.ink4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {photoFile ? photoFile.name : "Nenhum arquivo selecionado"}
                  </span>
                </label>
              </Field>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 8, lineHeight: 1.6 }}>
                Formato JPG ou PNG, até 2 MB.<br />
                Use imagem quadrada (ex.: 300×300 px) para evitar cortes.
              </div>
            </div>
            <div>
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 12, border: `1px solid ${T.hair}` }} />
              ) : (
                <div style={{ width: "100%", aspectRatio: "1", borderRadius: 12, border: `1.5px dashed ${T.hair}`, background: T.hair2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: T.ink4 }}>
                  Pré-visualização
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ display: "flex", gap: 8, paddingBottom: 24 }}>
          <button
            type="submit"
            disabled={loading}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 10, border: "none", background: loading ? T.ink4 : T.accent, color: "#fff", fontSize: 13.5, fontWeight: 500, cursor: loading ? "not-allowed" : "pointer", fontFamily: T.font }}>
            {loading
              ? <><Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> Salvando…</>
              : <><CheckCircleIcon style={{ width: 15, height: 15 }} /> Cadastrar parceiro</>}
          </button>
          <button
            type="button"
            onClick={goBack}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "transparent", color: T.ink2, fontSize: 13.5, cursor: "pointer", fontFamily: T.font }}>
            Cancelar
          </button>
        </div>

      </div>
    </form>
  );
}
